import { useState, useEffect } from 'react'
import { get, post, put, del } from '../utils/api'
import { IconPlus, IconEye, IconTrash, IconCash, IconX } from '@tabler/icons-react'

const STATUS_COLORS = { draft: 'bg-secondary-lt', sent: 'bg-blue-lt', viewed: 'bg-cyan-lt', partial: 'bg-yellow-lt', paid: 'bg-green-lt', overdue: 'bg-red-lt', cancelled: 'bg-red-lt' }

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([])
  const [events, setEvents] = useState([])
  const [contacts, setContacts] = useState([])
  const [selected, setSelected] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ contact_name: '', company: '', email: '', tax_rate: 0, due_date: '', notes: '', event_id: '', items: [{ description: '', quantity: 1, unit_price: 0 }] })
  const [payForm, setPayForm] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = () => Promise.all([get('/invoices'), get('/events'), get('/contacts')]).then(([i, e, c]) => { setInvoices(i); setEvents(e); setContacts(c) }).finally(() => setLoading(false))
  useEffect(() => { load() }, [])

  const selectInvoice = async (id) => { const d = await get(`/invoices/${id}`); setSelected(d); setShowForm(false) }

  const addLine = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0 }] })
  const removeLine = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) })
  const updateLine = (i, field, val) => { const items = [...form.items]; items[i] = { ...items[i], [field]: val }; setForm({ ...form, items }) }
  const subtotal = form.items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0)

  const save = async () => {
    try {
      const r = await post('/invoices', form)
      setShowForm(false)
      load()
      selectInvoice(r.id)
    } catch (err) { alert(err.message) }
  }

  const markSent = async () => { await put(`/invoices/${selected.id}`, { status: 'sent' }); selectInvoice(selected.id); load() }

  const addPayment = async () => {
    if (!payForm?.amount) return
    await post(`/invoices/${selected.id}/payments`, payForm)
    await post(`/invoices/${selected.id}/recalculate`)
    selectInvoice(selected.id)
    setPayForm(null)
    load()
  }

  const addItemToExisting = async () => {
    await post(`/invoices/${selected.id}/items`, { description: 'New item', quantity: 1, unit_price: 0 })
    await post(`/invoices/${selected.id}/recalculate`)
    selectInvoice(selected.id)
  }

  const removeExistingItem = async (itemId) => {
    await del(`/invoices/items/${itemId}`)
    await post(`/invoices/${selected.id}/recalculate`)
    selectInvoice(selected.id)
  }

  const fmt = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const balance = selected ? Number(selected.total || 0) - Number(selected.amount_paid || 0) : 0

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>

  return (
    <div className="row g-4">
      <div className="col-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h2 className="mb-0">Invoices</h2>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={() => { setShowForm(true); setSelected(null); setForm({ contact_name: '', company: '', email: '', tax_rate: 0, due_date: '', notes: '', event_id: '', items: [{ description: '', quantity: 1, unit_price: 0 }] }) }}><IconPlus size={16} /> New</button>
        </div>
        <div className="card">
          <div className="list-group list-group-flush">
            {invoices.map(inv => (
              <button key={inv.id} className={`list-group-item list-group-item-action ${selected?.id === inv.id ? 'active' : ''}`} onClick={() => selectInvoice(inv.id)}>
                <div className="d-flex justify-content-between">
                  <div>
                    <div className="fw-medium">{inv.invoice_number}</div>
                    <div style={{ fontSize: '0.8rem' }} className="text-secondary">{inv.company || inv.contact_name}</div>
                  </div>
                  <div className="text-end">
                    <div className="fw-medium">{fmt(inv.total)}</div>
                    <span className={`badge ${STATUS_COLORS[inv.status]}`}>{inv.status}</span>
                  </div>
                </div>
              </button>
            ))}
            {!invoices.length && <div className="list-group-item text-center text-secondary py-4">No invoices yet</div>}
          </div>
        </div>
      </div>

      <div className="col-lg-8">
        {showForm ? (
          <div className="card">
            <div className="card-header"><h3 className="card-title mb-0">New Invoice</h3></div>
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-6"><label className="form-label">Contact</label><input className="form-control" value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} /></div>
                <div className="col-6"><label className="form-label">Company</label><input className="form-control" value={form.company} onChange={e => setForm({...form, company: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">Email</label><input className="form-control" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">Due Date</label><input type="date" className="form-control" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">Tax %</label><input type="number" step="0.01" className="form-control" value={form.tax_rate} onChange={e => setForm({...form, tax_rate: parseFloat(e.target.value)||0})} /></div>
              </div>
              <h4 className="mb-2">Line Items</h4>
              {form.items.map((item, i) => (
                <div key={i} className="d-flex gap-2 mb-2 align-items-end">
                  <input className="form-control form-control-sm" placeholder="Description" value={item.description} onChange={e => updateLine(i, 'description', e.target.value)} style={{ flex: 3 }} />
                  <input type="number" className="form-control form-control-sm" placeholder="Qty" value={item.quantity} onChange={e => updateLine(i, 'quantity', parseFloat(e.target.value)||0)} style={{ width: 70 }} />
                  <input type="number" step="0.01" className="form-control form-control-sm" placeholder="Price" value={item.unit_price} onChange={e => updateLine(i, 'unit_price', parseFloat(e.target.value)||0)} style={{ width: 100 }} />
                  <span className="text-secondary" style={{ width: 80, textAlign: 'right', fontSize: '0.85rem' }}>{fmt(item.quantity * item.unit_price)}</span>
                  {form.items.length > 1 && <button className="btn btn-ghost-danger btn-sm btn-icon" onClick={() => removeLine(i)}><IconX size={14}/></button>}
                </div>
              ))}
              <button className="btn btn-ghost-primary btn-sm mb-3" onClick={addLine}>+ Add Line</button>
              <div className="text-end border-top pt-2">
                <div>Subtotal: <strong>{fmt(subtotal)}</strong></div>
                <div>Tax ({form.tax_rate}%): <strong>{fmt(subtotal * form.tax_rate / 100)}</strong></div>
                <div style={{ fontSize: '1.1rem' }}>Total: <strong>{fmt(subtotal + subtotal * form.tax_rate / 100)}</strong></div>
              </div>
            </div>
            <div className="card-footer d-flex justify-content-end gap-2">
              <button className="btn btn-ghost-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>Create Invoice</button>
            </div>
          </div>
        ) : selected ? (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div><h2 className="mb-0">{selected.invoice_number}</h2><span className="text-secondary">{selected.company || selected.contact_name}</span></div>
              <div className="d-flex gap-2">
                {selected.status === 'draft' && <button className="btn btn-blue btn-sm" onClick={markSent}>Mark Sent</button>}
                {balance > 0 && <button className="btn btn-green btn-sm d-flex align-items-center gap-1" onClick={() => setPayForm({ amount: balance, method: 'credit_card', reference: '', note: '' })}><IconCash size={16}/> Record Payment</button>}
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-3"><div className="card"><div className="card-body text-center py-3"><div className="text-secondary" style={{ fontSize: '0.8rem' }}>Total</div><div className="fw-bold" style={{ fontSize: '1.2rem' }}>{fmt(selected.total)}</div></div></div></div>
              <div className="col-md-3"><div className="card"><div className="card-body text-center py-3"><div className="text-secondary" style={{ fontSize: '0.8rem' }}>Paid</div><div className="fw-bold text-green" style={{ fontSize: '1.2rem' }}>{fmt(selected.amount_paid)}</div></div></div></div>
              <div className="col-md-3"><div className="card"><div className="card-body text-center py-3"><div className="text-secondary" style={{ fontSize: '0.8rem' }}>Balance</div><div className={`fw-bold ${balance > 0 ? 'text-red' : 'text-green'}`} style={{ fontSize: '1.2rem' }}>{fmt(balance)}</div></div></div></div>
              <div className="col-md-3"><div className="card"><div className="card-body text-center py-3"><div className="text-secondary" style={{ fontSize: '0.8rem' }}>Status</div><div><span className={`badge ${STATUS_COLORS[selected.status]}`} style={{ fontSize: '0.9rem' }}>{selected.status}</span></div></div></div></div>
            </div>

            {/* Line Items */}
            <div className="card mb-3">
              <div className="card-header d-flex justify-content-between"><h4 className="card-title mb-0">Line Items</h4></div>
              <div className="table-responsive">
                <table className="table table-vcenter card-table mb-0">
                  <thead><tr><th>Description</th><th className="text-end" style={{width:70}}>Qty</th><th className="text-end" style={{width:100}}>Price</th><th className="text-end" style={{width:100}}>Total</th><th style={{width:40}}></th></tr></thead>
                  <tbody>
                    {(selected.items||[]).map(item => (
                      <tr key={item.id}><td>{item.description}</td><td className="text-end">{item.quantity}</td><td className="text-end">{fmt(item.unit_price)}</td><td className="text-end fw-medium">{fmt(item.total)}</td>
                        <td><button className="btn btn-ghost-danger btn-sm btn-icon" onClick={() => removeExistingItem(item.id)}><IconX size={14}/></button></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-footer py-2 text-end">
                <span className="me-3">Subtotal: <strong>{fmt(selected.subtotal)}</strong></span>
                <span className="me-3">Tax: <strong>{fmt(selected.tax_amount)}</strong></span>
                <span>Total: <strong style={{ fontSize: '1.1rem' }}>{fmt(selected.total)}</strong></span>
              </div>
            </div>

            {/* Payments */}
            {(selected.payments?.length > 0 || payForm) && (
              <div className="card mb-3">
                <div className="card-header"><h4 className="card-title mb-0">Payments</h4></div>
                <div className="table-responsive">
                  <table className="table table-vcenter card-table mb-0">
                    <thead><tr><th>Date</th><th>Method</th><th>Reference</th><th className="text-end">Amount</th></tr></thead>
                    <tbody>
                      {(selected.payments||[]).map(p => (
                        <tr key={p.id}><td>{new Date(p.paid_at).toLocaleDateString()}</td><td>{p.method}</td><td>{p.reference || '--'}</td><td className="text-end text-green fw-medium">{fmt(p.amount)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Record Payment Modal */}
            {payForm && (
              <div className="card border-green">
                <div className="card-header bg-green-lt"><h4 className="card-title mb-0">Record Payment</h4></div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-4"><label className="form-label">Amount</label><input type="number" step="0.01" className="form-control" value={payForm.amount} onChange={e => setPayForm({...payForm, amount: parseFloat(e.target.value)||0})} /></div>
                    <div className="col-4"><label className="form-label">Method</label><select className="form-select" value={payForm.method} onChange={e => setPayForm({...payForm, method: e.target.value})}><option value="credit_card">Credit Card</option><option value="check">Check</option><option value="wire">Wire Transfer</option><option value="cash">Cash</option><option value="other">Other</option></select></div>
                    <div className="col-4"><label className="form-label">Reference</label><input className="form-control" value={payForm.reference} onChange={e => setPayForm({...payForm, reference: e.target.value})} placeholder="Check # / Ref" /></div>
                  </div>
                </div>
                <div className="card-footer d-flex justify-content-end gap-2">
                  <button className="btn btn-ghost-secondary" onClick={() => setPayForm(null)}>Cancel</button>
                  <button className="btn btn-green" onClick={addPayment}>Save Payment</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="card"><div className="card-body text-center text-secondary py-5"><IconEye size={48} style={{ opacity: 0.3 }} className="mb-3" /><p>Select an invoice or create a new one</p></div></div>
        )}
      </div>
    </div>
  )
}
